


































import { namespace } from 'vuex-class';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import { ActionSheetButton } from '@ionic/core/dist/types/components/action-sheet/action-sheet-interface';

import WatchNowButton from '@/components/buttons/WatchNowButton.vue';

import { ActionSheetEvents } from '@/enums/events';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { TitleListName } from '@/interfaces/responses/title-list';
import {
	PosterQuickActionGraphqlType,
	PosterQuickActionsGraphqlProps,
} from '@/interfaces/components/poster/PosterQuickActionsGraphql';
import { ObjectType } from '@/@types/graphql-types';

import { MarkAsSeenModalOptions, MarkAsSeenModalProps } from '@/components/mark-as-seen/MarkAsSeen';

import { ActionSheetHelper } from '@/helpers/action-sheet-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';
import TitleLikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleLikelistMutationMixin.vue';
import TitleDislikelistMutationMixin from '@/mixins/title-quick-action-mutations/TitleDislikelistMutationMixin.vue';
import TitleSeenlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleSeenlistMutationMixin.vue';

const TitlePosterQuickActionsContentGraphql = () =>
	import('@/components/poster/TitlePosterQuickActionsContentGraphql.vue');
const MarkAsSeenModalGraphql = () => import('@/components/mark-as-seen/MarkAsSeenModalContainerGraphql.vue');

const ModuleStore = namespace('module');

@Component({
	name: 'PosterQuickActionsGraphql',
	components: { TitlePosterQuickActionsContentGraphql, WatchNowButton },
})
export default class PosterQuickActionsGraphql extends Mixins(
	TitleWatchlistMutationMixin,
	TitleLikelistMutationMixin,
	TitleDislikelistMutationMixin,
	TitleSeenlistMutationMixin
) {
	expanded = false;

	@Prop({ required: true }) titleId: PosterQuickActionsGraphqlProps['titleId'];
	@Prop({ required: true }) titleObjectId: PosterQuickActionsGraphqlProps['titleObjectId'];
	@Prop({ required: true }) titleObjectType: PosterQuickActionsGraphqlProps['titleObjectType'];

	@Prop({ default: false }) forceRender: PosterQuickActionsGraphqlProps['forceRender'];
	@Prop({ default: true }) showWatchNowButton: PosterQuickActionsGraphqlProps['showWatchNowButton'];
	@Prop({ default: false }) hasMoreLikeThis: PosterQuickActionsGraphqlProps['hasMoreLikeThis'];
	@Prop({ default: false }) isInWatchlist: NonNullable<PosterQuickActionsGraphqlProps['isInWatchlist']>;
	@Prop({ default: false }) isInLikeList: NonNullable<PosterQuickActionsGraphqlProps['isInLikeList']>;
	@Prop({ default: false }) isInDislikeList: NonNullable<PosterQuickActionsGraphqlProps['isInDislikeList']>;
	@Prop({ default: false }) isInSeenList: NonNullable<PosterQuickActionsGraphqlProps['isInSeenList']>;
	@Prop({ default: true }) isTitleReleased: PosterQuickActionsGraphqlProps['isTitleReleased'];
	@Prop({ default: '' }) titleName: NonNullable<PosterQuickActionsGraphqlProps['titleName']>;
	@Prop({ default: '' }) titlePosterUrl: PosterQuickActionsGraphqlProps['titlePosterUrl'];
	@Prop({ default: '' }) titleBackdrops: PosterQuickActionsGraphqlProps['titleBackdrops'];
	@Prop({ default: null }) titleSeenEpisodeCount: PosterQuickActionsGraphqlProps['titleSeenEpisodeCount'];
	@Prop({ default: null }) titleScores: PosterQuickActionsGraphqlProps['titleScores'];
	@Prop({ default: null }) titleSeasonNumber: PosterQuickActionsGraphqlProps['titleSeasonNumber'];
	@Prop({ default: null }) watchNowOffer: PosterQuickActionsGraphqlProps['watchNowOffer'];
	@Prop({ default: false }) isSimilarTitle: PosterQuickActionsGraphqlProps['isSimilarTitle'];
	@Prop({ default: false }) smallBubbles: PosterQuickActionsGraphqlProps['isSimilarTitle'];

	@ModuleStore.Action addMoreLikeThis: (payload: { id: string }) => void;
	@ModuleStore.Action setActiveModuleIndex: (activeModule: number | null) => void;
	@ModuleStore.Action setInteractedModuleIndex: (interactedModule: number | null) => void;

	private get watchlistText() {
		if (!this.isTvShowOrSeason && this.titleObjectType != ObjectType.ShowEpisode) {
			return this.$t('WEBAPP_WATCHLIST');
		}

		return this.isInWatchlist ? this.$t('WEBAPP_TRACKING') : this.$t('WEBAPP_WATCHLIST_TV_SHOW_START_TRACKING');
	}
	private get watchlistRemoveText() {
		if (!this.isTvShowOrSeason && this.titleObjectType != ObjectType.ShowEpisode) {
			return this.$t('WEBAPP_WATCHLIST');
		} else {
			return this.isTouchDevice ? this.$t('WEBAPP_WATCHLIST_REMOVE_TVSHOW') : this.$t('WEBAPP_TRACKING');
		}
	}

	get isTouchDevice() {
		return process.client && IS_TOUCH;
	}

	get isTvShowOrSeason() {
		return this.titleObjectType === ObjectType.Show || this.titleObjectType === ObjectType.ShowSeason;
	}

	get moreLikeThisOption() {
		return { text: `${this.$t('WEBAPP_MORE_LIKE_THIS')}...`, name: TitleListName.MORELIST };
	}

	get options(): PosterQuickActionGraphqlType[] {
		const options: PosterQuickActionGraphqlType[] = [];
		options.push({
			text: this.isInWatchlist ? this.watchlistRemoveText : this.watchlistText,
			icon: 'bookmark',
			isActive: this.isInWatchlist,
			class: 'title-poster-quick-actions__watchlist',
			name: TitleListName.WATCHLIST,
		});
		if (this.titleObjectType === ObjectType.Movie) {
			options.push({
				text: this.isInSeenList ? this.$t('WEBAPP_SET_UNSEEN_WATCHLIST') : this.$t('WEBAPP_SEENLIST_SEEN'),
				icon: 'jw-check-icon',
				class: 'title-poster-quick-actions__seen-list',
				isActive: this.isInSeenList,
				name: TitleListName.SEENLIST,
			});
		}
		options.push({
			text: this.isInLikeList ? this.$t('WEBAPP_REMOVE_FROM_LIKELIST') : this.$t('WEBAPP_LIKE'),
			icon: 'thumbs-up',
			class: 'title-poster-quick-actions__like',
			isActive: this.isInLikeList,
			name: TitleListName.LIKELIST,
		});
		options.push({
			text: this.isInDislikeList ? this.$t('WEBAPP_REMOVE_FROM_DISLIKELIST') : this.$t('WEBAPP_DISLIKE'),
			icon: 'thumbs-up',
			iconClass: ['fa-rotate-180'],
			class: 'title-poster-quick-actions__dislike',
			isActive: this.isInDislikeList,
			name: TitleListName.DISLIKELIST,
		});
		return options;
	}

	get hasOptionSelected(): boolean {
		return this.isInWatchlist || this.isInLikeList || this.isInDislikeList || this.isInSeenList;
	}

	quickActionSheetButtons(evt: Event) {
		const extraOptions = [...this.options];
		if (this.hasMoreLikeThis) {
			// extraOptions.push({ ...this.moreLikeThisOption });
		}

		// Transform the options QuickActionOptions into ActionSheetButton
		const buttons: ActionSheetButton[] = extraOptions.map(button => {
			const isOptionActive = button.isActive;
			return {
				...button,
				text: button.text as string,
				handler: () => this.selectOption(evt, button),
				cssClass: isOptionActive ? 'action-sheet-active' : '',
				icon: undefined, // remove the original icon
			};
		});

		// Adding the cancel button in the end
		buttons.push({ text: String(this.$t('WEBAPP_CANCEL')), role: 'cancel' });
		return buttons;
	}

	/**
	 * toggle quick action panel and load title if needed
	 *
	 * @param {Event} evt click event
	 *
	 */
	toggleExpand(evt: Event) {
		evt.stopPropagation();
		evt.preventDefault();
		this.expanded = !this.expanded;
	}

	openQuickActionsActionSheet(evt: Event) {
		const onCloseCallback = () => this.trackEvent(ActionSheetEvents.CLOSED);

		ActionSheetHelper.openActionSheet(
			{
				header: this.titleName,
				// TODO [GraphQL]: Use season number when we will get it from response in New
				// header:
				// 	this.title.objectType === ObjectType.ShowSeason
				// 		? `S${this.title.season_number} - ${this.title.show_title}`
				// 		: this.title.content.title,
				buttons: this.quickActionSheetButtons(evt),
			},
			onCloseCallback
		);

		this.trackEvent(ActionSheetEvents.OPENED);
	}

	trackEvent(label: string) {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'action_sheet',
			label,
			property: 'quick_actions',
		});
	}

	collapse() {
		this.expanded = false;
		this.setActiveModuleIndex(null);
	}

	@Watch('isLoggedIn', { immediate: true })
	onIsLoggedInChange() {
		const titleDetails = {
			title: this.titleName,
			objectType: this.titleObjectType,
			objectId: this.titleObjectId,
		};

		if (this.isLoggedIn())
			this.mixin_onIsLoggedInChange(
				this.titleId,
				titleDetails,
				this.isInWatchlist,
				this.isInWatchlist ? 'quick-actions-2nd-bubble' : 'quick-actions'
			);
	}

	async selectOption($event: Event, option: PosterQuickActionGraphqlType) {
		$event.preventDefault();
		$event.stopPropagation();

		const source = option.isActive ? 'quick-actions-2nd-bubble' : 'quick-actions';

		const titleDetails = {
			title: this.titleName,
			objectType: this.titleObjectType,
			objectId: this.titleObjectId,
		};

		const trackingPayload = {
			source,
			property: this.isSimilarTitle ? 'similartitles' : undefined,
		};

		switch (option.name) {
			case TitleListName.WATCHLIST: {
				const updatedTitle = await this.mixin_setInWatchlist(
					this.titleId,
					option.isActive,
					titleDetails,
					trackingPayload
				);

				if (updatedTitle) {
					const isInWatchlist = !!updatedTitle.data?.setInWatchlist.title.watchlistEntry?.createdAt;

					if (isInWatchlist && this.isTvShowOrSeason && this.isTitleReleased) {
						await ModalHelper.openModal(
							MarkAsSeenModalGraphql,
							{
								titleId: this.titleId,
								titleName: this.titleName,
								titleObjectId: this.titleObjectId,
								titleObjectType: this.titleObjectType,
								posterUrl: this.titlePosterUrl,
								backdrops: this.titleBackdrops,
								seenEpisodeCount: this.titleSeenEpisodeCount,
							} as MarkAsSeenModalProps,
							MarkAsSeenModalOptions
						);
					}
				}

				break;
			}
			case TitleListName.SEENLIST: {
				await this.mixin_setInSeenlist(this.titleId, option.isActive, titleDetails, undefined, trackingPayload);
				break;
			}
			case TitleListName.LIKELIST:
				await this.mixin_setInLikelist(
					this.titleId,
					option.isActive,
					!option.isActive && this.isInDislikeList,
					titleDetails,
					trackingPayload
				);

				break;
			case TitleListName.DISLIKELIST:
				await this.mixin_setInDislikelist(
					this.titleId,
					option.isActive,
					!option.isActive && this.isInLikeList,
					titleDetails,
					trackingPayload
				);
				break;
			case TitleListName.MORELIST:
				// TODO [GraphQL] Need to be fixed during final cleanup
				//@ts-ignore
				this.setInteractedModuleIndex(this.additionalContexts[0]?.moduleIdx);
				const titleContext = new SnowplowTitleContextGraphql(titleDetails.objectId, titleDetails.objectType);
				TrackingHelper.trackListEvent(
					TitleListName.MORELIST,
					'add',
					{ action: trackingPayload.source, property: trackingPayload.property },
					[...this.additionalContexts, titleContext]
				);

				this.addMoreLikeThis({
					id: this.titleId,
				});
				break;
			default:
				return;
		}
	}
}
